import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const AddOrEditCouponForm = ({ open, onClose, initialData, onSubmit, services = [], categories = [], products = [] }) => {
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: 0,
    minimumOrderValue: 0,
    maximumDiscount: "",
    validFrom: new Date(),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    usageLimit: 1,
    applicableTo: {
      type: "all",
      items: [],
    },
    applicableUserType: "all",
    specificUsers: [],
    oneTimeUse: false,
    isActive: true,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        validFrom: new Date(initialData.validFrom),
        validUntil: new Date(initialData.validUntil),
        applicableTo: initialData.applicableTo || { type: "all", items: [] },
        specificUsers: initialData.specificUsers || [],
        maximumDiscount: initialData.maximumDiscount || "",
      });
    } else {
      setFormData({
        code: "",
        description: "",
        discountType: "percentage",
        discountValue: 0,
        minimumOrderValue: 0,
        maximumDiscount: "",
        validFrom: new Date(),
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        usageLimit: 1,
        applicableTo: {
          type: "all",
          items: [],
        },
        applicableUserType: "all",
        specificUsers: [],
        oneTimeUse: false,
        isActive: true,
      });
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplicableToChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      applicableTo: {
        ...prev.applicableTo,
        [field]: value,
        ...(field === 'type' && value === 'all' ? { items: [] } : {}), // Clear items when type changes to 'all'
      },
    }));
  };

  const handleAddSpecificUser = () => {
    setFormData((prev) => ({
      ...prev,
      specificUsers: [...prev.specificUsers, ""],
    }));
  };

  const handleSpecificUserChange = (index, value) => {
    const updated = [...formData.specificUsers];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, specificUsers: updated }));
  };

  const handleRemoveSpecificUser = (index) => {
    const updated = [...formData.specificUsers];
    updated.splice(index, 1);
    setFormData((prev) => ({ ...prev, specificUsers: updated }));
  };

  const handleApplicableItemsChange = (selectedItems) => {
    handleApplicableToChange("items", selectedItems);
  };

  const getApplicableItemsOptions = () => {
    switch (formData.applicableTo.type) {
      case "specific_services":
        return services;
      case "specific_categories":
        return categories;
      case "specific_products":
        return products;
      default:
        return [];
    }
  };

  const calculateFinalDiscount = (orderAmount = 1000) => {
    let discount = 0;
    switch (formData.discountType) {
      case "percentage":
        discount = (orderAmount * formData.discountValue) / 100;
        if (formData.maximumDiscount && discount > formData.maximumDiscount) {
          discount = formData.maximumDiscount;
        }
        break;
      case "fixed":
        discount = Math.min(formData.discountValue, orderAmount);
        break;
      case "free_service":
        discount = orderAmount;
        break;
      default:
        discount = 0;
    }
    return discount;
  };

  const handleSubmit = async () => {
    const payload = {
      ...formData,
      code: formData.code.toUpperCase(),
      maximumDiscount: formData.maximumDiscount || undefined,
      discountValue: parseFloat(formData.discountValue),
      minimumOrderValue: parseFloat(formData.minimumOrderValue),
      usageLimit: parseInt(formData.usageLimit),
    };

    await onSubmit(payload);
  };

  const handleClose = () => {
    onClose();
    if (!initialData) {
      setFormData({
        code: "",
        description: "",
        discountType: "percentage",
        discountValue: 0,
        minimumOrderValue: 0,
        maximumDiscount: "",
        validFrom: new Date(),
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        usageLimit: 1,
        applicableTo: {
          type: "all",
          items: [],
        },
        applicableUserType: "all",
        specificUsers: [],
        oneTimeUse: false,
        isActive: true,
      });
    }
  };

  const applicableItemsOptions = getApplicableItemsOptions();
  const previewDiscount = calculateFinalDiscount();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{initialData ? "Edit Coupon" : "Create New Coupon"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Basic Information */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Coupon Code"
                value={formData.code}
                onChange={(e) => handleChange("code", e.target.value)}
                fullWidth
                margin="normal"
                required
                helperText="Unique code for the coupon"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                fullWidth
                margin="normal"
                required
                multiline
                rows={2}
              />
            </Grid>

            {/* Discount Configuration */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Discount Type</InputLabel>
                <Select
                  value={formData.discountType}
                  onChange={(e) => handleChange("discountType", e.target.value)}
                  label="Discount Type"
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                  <MenuItem value="free_service">Free Service</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label={formData.discountType === "percentage" ? "Discount Percentage" : "Discount Amount"}
                type="number"
                value={formData.discountValue}
                onChange={(e) => handleChange("discountValue", e.target.value)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  endAdornment: formData.discountType === "percentage" ? "%" : "₹",
                }}
              />
            </Grid>

            {formData.discountType === "percentage" && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Maximum Discount"
                  type="number"
                  value={formData.maximumDiscount}
                  onChange={(e) => handleChange("maximumDiscount", e.target.value)}
                  fullWidth
                  margin="normal"
                  helperText="Maximum discount amount (optional)"
                  InputProps={{
                    endAdornment: "₹",
                  }}
                />
              </Grid>
            )}

            {/* Order Requirements */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Minimum Order Value"
                type="number"
                value={formData.minimumOrderValue}
                onChange={(e) => handleChange("minimumOrderValue", e.target.value)}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: "₹",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Usage Limit"
                type="number"
                value={formData.usageLimit}
                onChange={(e) => handleChange("usageLimit", e.target.value)}
                fullWidth
                margin="normal"
                helperText="Maximum number of times this coupon can be used"
              />
            </Grid>

            {/* Validity Period */}
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Valid From"
                value={formData.validFrom}
                onChange={(date) => handleChange("validFrom", date)}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Valid Until"
                value={formData.validUntil}
                onChange={(date) => handleChange("validUntil", date)}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                minDate={formData.validFrom}
              />
            </Grid>

            {/* Applicable To */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Applicable To</InputLabel>
                <Select
                  value={formData.applicableTo.type}
                  onChange={(e) => handleApplicableToChange("type", e.target.value)}
                  label="Applicable To"
                >
                  <MenuItem value="all">All Services/Products</MenuItem>
                  <MenuItem value="specific_services">Specific Services</MenuItem>
                  <MenuItem value="specific_categories">Specific Categories</MenuItem>
                  <MenuItem value="specific_products">Specific Products</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.applicableTo.type !== "all" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>
                    {formData.applicableTo.type === "specific_services" && "Select Services"}
                    {formData.applicableTo.type === "specific_categories" && "Select Categories"}
                    {formData.applicableTo.type === "specific_products" && "Select Products"}
                  </InputLabel>
                  <Select
                    multiple
                    value={formData.applicableTo.items}
                    onChange={(e) => handleApplicableItemsChange(e.target.value)}
                    label={
                      formData.applicableTo.type === "specific_services" ? "Select Services" :
                        formData.applicableTo.type === "specific_categories" ? "Select Categories" :
                          "Select Products"
                    }
                  >
                    {applicableItemsOptions.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* User Restrictions */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Applicable User Type</InputLabel>
                <Select
                  value={formData.applicableUserType}
                  onChange={(e) => handleChange("applicableUserType", e.target.value)}
                  label="Applicable User Type"
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="new_user">New Users Only</MenuItem>
                  <MenuItem value="existing_user">Existing Users Only</MenuItem>
                  <MenuItem value="specific_users">Specific Users</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.applicableUserType === "specific_users" && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Specific Users (User IDs)
                </Typography>
                {formData.specificUsers.map((userId, index) => (
                  <Grid container spacing={1} key={index} alignItems="center">
                    <Grid item xs={10}>
                      <TextField
                        label={`User ID ${index + 1}`}
                        value={userId}
                        onChange={(e) => handleSpecificUserChange(index, e.target.value)}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton onClick={() => handleRemoveSpecificUser(index)}>
                        <RemoveCircleOutline />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button onClick={handleAddSpecificUser} startIcon={<AddCircleOutline />} color="primary">
                  Add User
                </Button>
              </Grid>
            )}

            {/* Settings */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.oneTimeUse}
                    onChange={(e) => handleChange("oneTimeUse", e.target.checked)}
                  />
                }
                label="One-time use per user"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                  />
                }
                label="Active Coupon"
              />
            </Grid>

            {/* Preview */}
            {/* <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Discount Preview
              </Typography>
              <Typography variant="body2" color="textSecondary">
                On an order of ₹1000:{" "}
                <strong>₹{previewDiscount && previewDiscount?.toFixed(2)} discount</strong> • Final amount:{" "}
                <strong>₹{previewDiscount && (1000 - previewDiscount)?.toFixed(2)}</strong>
              </Typography>
            </Grid> */}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {initialData ? "Update Coupon" : "Create Coupon"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddOrEditCouponForm;